import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Button, Form, Spinner } from "react-bootstrap";
import payrollData from "../../../store/jsondata/payrollData.json";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import usePayrollApi from "../../../services/payroll/payrollApi";
import useHrApi from "../../../services/hr/hrAPI";
import { toast } from "react-toastify";
import { GetCurrentUserName } from "../../../utils/permissionChecker";
import moment from "moment";
import SearchableDropdown from "../../../components/UiComponent/SearchableDropdown";
const AddDeduction = ({ closeModal, initialValues }) => {
  const { t } = useTranslation();
  const loggedInUser = GetCurrentUserName();
  const { addDeduction, updateDeduction } = usePayrollApi(); // Assuming you have an updateDeduction function
  const { getAllEmployee } = useHrApi();
  const queryClient = useQueryClient();
  const mutationFunction = initialValues ? updateDeduction : addDeduction; // Determine whether to add or update
  const { mutate: BenefitMutation } = useMutation(mutationFunction);
  const getGradeQuery = useQuery(["get_all_employee"], () => getAllEmployee());
  if (getGradeQuery.isLoading) {
    return <div>Loading...</div>;
  }

  if (getGradeQuery.isError) {
    return <div>Error: Unable to fetch data</div>;
  }
  const grade = getGradeQuery.data;
  const validationSchema = Yup.object().shape({
    from_date: Yup.string().trim().required("Applicable date is required"),
    to_date: Yup.string()
      .trim()
      .required("End date is required")
      .test("end-date", "End date must be after start date", function (value) {
        const { from_date } = this.parent;
        if (!value || !from_date) return true;
        return new Date(value) > new Date(from_date);
      }),
    deduction_name: Yup.string().trim().required("Deduction name is required"),
    deduction_description: Yup.string().required(
      "Deduction description name required"
    ),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .required("Amount is required")
      .positive("Amount must be a positive number"),
    emp_id: Yup.string().required("Employee name is required"),
    is_percentage: Yup.string().required("Deduction payment type is required"),
  });

  const onSubmit = async (values) => {
    try {
      BenefitMutation(values, {
        onSuccess: () => {
          closeModal();
          queryClient.invalidateQueries("benefit_list");
          toast.success(
            initialValues
              ? "Deduction updated successfully"
              : "New Deduction registered successfully"
          );
        },
        onError: (error) => {
          toast.error(
            initialValues
              ? `Failed to update deduction: ${error?.message}`
              : `Failed to register new deduction: ${error?.message}`
          );
        },
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col lg="12">
          <Formik
            initialValues={
              initialValues || {
                from_date: moment(new Date()).format("YYYY-MM-DD"),
                to_date: moment(new Date()).format("YYYY-MM-DD"),
                amount: "",
                status: 1,
                is_percentage: "",
                emp_id: "",
                deduction_name: "",
                deduction_description: "",
                created_by: loggedInUser,
                updated_by: loggedInUser,
              }
            }
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formikProps) => (
              <Form noValidate onSubmit={formikProps.handleSubmit}>
                <Form.Group controlId="deduction_name">
                  <Form.Label>{t("deduction_name")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="deduction_name"
                    value={formikProps.values.deduction_name}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.deduction_name &&
                      !!formikProps.errors.deduction_name
                    }
                    placeholder={t("deduction_name")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.deduction_name}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label className="text-secondary">
                    {t("deduction_description")}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="deduction_description"
                    value={formikProps.values.deduction_description}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.deduction_description &&
                      !!formikProps.errors.deduction_description
                    }
                    placeholder={t("deduction_description")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.deduction_description}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="emp_id">
                  <Form.Label>{t("employee")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="emp_id"
                    value={formikProps.values.emp_id}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.emp_id && !!formikProps.errors.emp_id
                    }
                  >
                    <option value="" disabled>
                      {t("select_employee")}
                    </option>
                    {grade &&
                      grade.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.first_name} {option.father_name}
                        </option>
                      ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.emp_id}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="amount">
                  <Form.Label>{t("amount")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="amount"
                    value={formikProps.values.amount}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.amount && !!formikProps.errors.amount
                    }
                    placeholder={t("amount")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.amount}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="is_percentage">
                  <Form.Label>{t("deduction_type")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="is_percentage"
                    value={formikProps.values.is_percentage}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.is_percentage &&
                      !!formikProps.errors.is_percentage
                    }
                  >
                    <option value="" disabled>
                      {t("deduction_type")}
                    </option>
                    {payrollData.benefit_type.map((data) => (
                      <option
                        key={data.is_percentage}
                        value={data.is_percentage}
                      >
                        {t(data.name)}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.is_percentage}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="from_date">
                  <Form.Label>{t("Starting Date")}</Form.Label>
                  <Form.Control
                    type="date"
                    name="from_date"
                    value={moment(
                      new Date(formikProps.values.from_date)
                    ).format("YYYY-MM-DD")}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.from_date &&
                      !!formikProps.errors.from_date
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.from_date}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="to_date">
                  <Form.Label>{t("End Date")}</Form.Label>
                  <Form.Control
                    type="date"
                    name="to_date"
                    value={moment(new Date(formikProps.values.to_date)).format(
                      "YYYY-MM-DD"
                    )}
                    onChange={formikProps.handleChange}
                    onBlur={formikProps.handleBlur}
                    isInvalid={
                      formikProps.touched.to_date &&
                      !!formikProps.errors.to_date
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formikProps.errors.to_date}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  className="mr-2"
                  variant="secondary"
                  onClick={closeModal}
                >
                  {t("close")}
                </Button>
                <Button
                  className=" btn btn-info mr-1"
                  variant="primary"
                  type="submit"
                  disabled={formikProps.isSubmitting} // Disable the button while submitting
                >
                  {formikProps.isSubmitting ? (
                    <span>
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="false"
                      />
                      <span className="visually-hidden">{t("Sending...")}</span>
                    </span>
                  ) : initialValues ? (
                    t("update")
                  ) : (
                    t("register")
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};

export default AddDeduction;
